import type { ModalInstance } from '../../contexts/ModalContext';
import type {
  ContentObject,
  ContentTypeDefinition,
  WithOptionalInternalProps,
  WithoutInternalProps,
} from '../../lib/flotiq-client/types/api-response';
import type {
  ContentObjectCustomValidateFn,
  ContentObjectSubmitFn,
} from '../ContentObjectForm/ContentObjectForm';

import { use, useCallback, useId, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import CancelButton from '../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../components/Button/predefined/SaveButton/SaveButton';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import ContentObjectForm from '../ContentObjectForm/ContentObjectForm';

type SchemaModalFormConfigOptions = {
  /**
   * Function that will be called on form validate. If there will be no result,
   * there will be yup validation based on schema. If returns errors, they will be passed
   * to form. Errors should be an object with field names as flat keys and error messages as values.
   */
  onValidate?: ContentObjectCustomValidateFn;
  /**
   * Function that will be called on form submit. Should return array with new object data
   * and errors. If not provided on form submission, the modal will be resolved with form values.
   */
  onSubmit?: (
    values: WithoutInternalProps<ContentObject>,
    modalInstance: ModalInstance,
  ) => ReturnType<ContentObjectSubmitFn>;
  /**
   * If build in validation should be disabled.
   * If build in validation is disabled, the validation has to be handled by onValidate option.
   */
  disabledBuildInValidation?: boolean;
};

type SchemaModalFormConfigLabels = {
  /**
   * Custom label for submit button.
   * @default "Ok"
   */
  ok?: string;
  /**
   * Custom label for cancel button.
   * @default "Cancel"
   */
  cancel?: string;
};

export type SchemaModalFormConfig = {
  /**
   * Schema object compatible flotiq api.
   * All passed schema gets nonCtdSchema value set on true.
   */
  schema: WithOptionalInternalProps<ContentTypeDefinition>;
  /**
   * Initial data for form
   */
  initialData?: WithOptionalInternalProps<ContentObject>;
  /**
   * Additional options passed to settings form
   */
  options?: SchemaModalFormConfigOptions;
  /**
   * Custom labels for modal buttons
   */
  labels?: SchemaModalFormConfigLabels;
};

interface SchemaModalContentProps {
  /**
   * Form data to render
   */
  form: SchemaModalFormConfig;
  /**
   * Form test id
   */
  testId?: string;
}

const SchemaModalContent = ({ form, testId = '' }: SchemaModalContentProps) => {
  const { initialData, schema, options, labels } = form;

  const modalInstance = use(ModalInstanceContext);
  const formId = useId();
  const [isSaving, setIsSaving] = useState(false);

  const closeModal = useCallback(
    (values: WithoutInternalProps<ContentObject> | null) => {
      modalInstance.resolve?.(values);
    },
    [modalInstance],
  );

  const handleSubmit = useCallback<ContentObjectSubmitFn>(
    async (values) => {
      setIsSaving(true);

      if (options?.onSubmit) {
        const result = await options.onSubmit(values, modalInstance);

        setIsSaving(false);
        return result;
      } else {
        modalInstance.resolve?.(values);

        return [values, null, false];
      }
    },
    [modalInstance, options],
  );

  return (
    <>
      <ContentObjectForm
        contentObject={initialData}
        contentType={{ ...schema, nonCtdSchema: true }}
        onSubmit={handleSubmit}
        onValidate={options?.onValidate}
        disabledBuildInValidation={options?.disabledBuildInValidation}
        formId={formId}
        disabled={isSaving}
        additionalFormClasses="pb-0 md:space-y-3"
        isEditing
        {...getTestProps(testId, 'co-form', 'testId')}
      />
      <div
        className={twMerge(
          'w-full absolute left-0 bottom-0 flex items-center justify-between p-3 md:px-10 space-x-5',
          'border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900',
        )}
      >
        <CancelButton
          onClick={() => closeModal(null)}
          disabled={isSaving}
          label={labels?.cancel}
          {...getTestProps(testId, 'cancel', 'testId')}
        />
        <SaveButton
          form={formId}
          isSaving={isSaving}
          label={labels?.ok}
          {...getTestProps(testId, 'submit', 'testId')}
        />
      </div>
    </>
  );
};

export default SchemaModalContent;
